/**
 * Standard-UCI parser + UCI->SAN conversion (Workstream B3).
 *
 * The old custom WASM emitted non-standard fields (`baseTurn`, `pvSan`, `bmc`)
 * that the old parser depended on. A stock SF18 build emits ONLY standard UCI:
 *
 *   info depth 20 seldepth 30 multipv 1 score cp 34 nodes ... pv e2e4 e7e5 g1f3
 *   info depth 18 multipv 2 score mate 3 ... pv d1h5 g8f6 h5f7
 *
 * This module:
 *   - parses `multipv N`, `score cp|mate X`, and the `pv <uci moves>` tail;
 *   - converts the UCI pv to SAN client-side via chess.js;
 *   - derives the side-to-move (`baseTurn`) from the FEN, not a custom field.
 *
 * It produces exactly the arguments the existing `onResult` contract expects:
 *   onResult(variationNumber, movesSAN, baseTurn, score, isMate, raw)
 * so all consumers (PlayChess, MovesList, StockfishComponent, Puzzles, Study,
 * ChessOverlayComponent) keep working unchanged.
 */

import {Chess} from 'chess.js';

export interface ParsedInfo {
  /** UCI `multipv N` -> the variation number consumers key on. */
  variationNumber: number;
  /** Principal variation, converted to SAN. */
  movesSAN: string[];
  /** Principal variation in raw UCI move notation. */
  movesUci: string[];
  /** 'w' | 'b', derived from the analyzed FEN's side-to-move field. */
  baseTurn: 'w' | 'b';
  /**
   * Score. Centipawns when !isMate, mate-distance (signed) when isMate.
   * Kept as a string to mirror the old contract (consumers do Number(...)).
   * NOTE: standard UCI scores are from the side-to-move's POV; consumers
   * (StockfishComponent) already flip for baseTurn === 'b', matching the
   * prior behaviour, so we pass the raw side-to-move score through.
   */
  score: string;
  /** True when the score is a mate score. */
  isMate: boolean;
}

/** Derive 'w'/'b' from a FEN's active-color field. Defaults to 'w'. */
export function baseTurnFromFen(fen: string): 'w' | 'b' {
  const parts = (fen || '').trim().split(/\s+/);
  return parts[1] === 'b' ? 'b' : 'w';
}

/**
 * Convert a UCI principal variation (e.g. ["e2e4","e7e5","g1f3"]) into SAN
 * (["e4","e5","Nf3"]) by replaying it on a chess.js instance seeded from `fen`.
 * Stops at the first move chess.js rejects (truncated / illegal pv tail) and
 * returns the SAN collected so far — never throws.
 */
export function uciPvToSan(fen: string, uciMoves: string[]): string[] {
  const san: string[] = [];
  let chess: Chess;
  try {
    chess = new Chess(fen);
  } catch {
    return san;
  }
  for (const uci of uciMoves) {
    if (!uci || uci.length < 4) {
      break;
    }
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    try {
      // chess.js@1.0.0-beta.8: move() returns the Move (with .san) or throws
      // on an illegal move. Object form takes UCI-style from/to/promotion.
      const move = chess.move(promotion ? {from, to, promotion} : {from, to}, {
        strict: false,
      } as any);
      if (move && (move as any).san) {
        san.push((move as any).san);
      } else {
        break;
      }
    } catch {
      break;
    }
  }
  return san;
}

/**
 * Parse one standard-UCI `info ... pv ...` line for the given analyzed FEN.
 * Returns null for lines that aren't a multipv info line with a pv.
 */
export function parseInfoLine(raw: string, fen: string): ParsedInfo | null {
  if (!raw || !raw.startsWith('info') || !raw.includes(' pv ')) {
    return null;
  }

  // multipv defaults to 1 when omitted (single-PV searches don't print it).
  const mpvMatch = raw.match(/\bmultipv (\d+)/);
  const variationNumber = mpvMatch ? parseInt(mpvMatch[1], 10) : 1;

  const cpMatch = raw.match(/\bscore cp ([-+]?\d+)/);
  const mateMatch = raw.match(/\bscore mate ([-+]?\d+)/);

  let score = '0';
  let isMate = false;
  if (mateMatch) {
    score = mateMatch[1];
    isMate = true;
  } else if (cpMatch) {
    score = cpMatch[1];
  }

  // The pv is everything after the ` pv ` token (UCI guarantees pv is last).
  const pvIndex = raw.indexOf(' pv ');
  const pvStr = raw.slice(pvIndex + 4).trim();
  const uciMoves = pvStr.length ? pvStr.split(/\s+/) : [];

  const baseTurn = baseTurnFromFen(fen);
  const movesSAN = uciPvToSan(fen, uciMoves);

  return {
    variationNumber,
    movesSAN,
    movesUci: uciMoves,
    baseTurn,
    score,
    isMate,
  };
}
